// =============================================
// TEMPLATES DE FLUXOS PRÉ-CONFIGURADOS
// =============================================
// Arquivo: flowTemplates.ts
// Descrição: Templates prontos para uso comum
// =============================================

import { Node, Edge } from 'reactflow';

export interface FlowTemplate {
  id: string;
  name: string;
  description: string;
  category: 'atendimento' | 'vendas' | 'suporte' | 'marketing' | 'frota' | 'outros';
  icon: string;
  nodes: Node[];
  edges: Edge[];
  tags: string[];
}

// Constantes de espaçamento (exportadas para uso em outros componentes)
export const FLOW_SPACING = {
  VERTICAL: 200,      // Espaçamento vertical entre nodes
  HORIZONTAL: 350,    // Espaçamento horizontal para branches
  START_X: 400,       // Posição X inicial
  START_Y: 0          // Posição Y inicial
};

// =============================================
// TEMPLATES
// =============================================

export const flowTemplates: FlowTemplate[] = [
  {
    id: 'welcome-flow',
    name: 'Boas-vindas Automático',
    description: 'Mensagem de boas-vindas com menu de opções para novos contatos',
    category: 'atendimento',
    icon: 'hand-wave',
    tags: ['básico', 'boas-vindas', 'menu'],
    nodes: [
      {
        id: 'start-1',
        type: 'start',
        position: { x: 100, y: 100 },
        data: { label: 'Início' }
      },
      {
        id: 'message-1',
        type: 'message',
        position: { x: 100, y: 200 },
        data: {
          label: 'Mensagem de Boas-vindas',
          message: '👋 Olá! Seja bem-vindo(a)!\n\nEu sou o assistente virtual. Como posso ajudar você hoje?',
          description: 'Mensagem inicial de boas-vindas'
        }
      },
      {
        id: 'input-1',
        type: 'input',
        position: { x: 100, y: 320 },
        data: {
          label: 'Menu de Opções',
          message: 'Digite uma opção:\n\n1️⃣ Falar com atendente\n2️⃣ Ver produtos\n3️⃣ Suporte técnico\n4️⃣ Horário de funcionamento',
          inputType: 'text',
          validation: 'none',
          variable: 'opcao_menu',
          description: 'Menu principal'
        }
      },
      {
        id: 'condition-1',
        type: 'condition',
        position: { x: 100, y: 460 },
        data: {
          label: 'Verificar Opção',
          variable: 'opcao_menu',
          operator: 'equals',
          value: '1',
          description: 'Roteia para opção escolhida'
        }
      },
      {
        id: 'message-2',
        type: 'message',
        position: { x: -100, y: 580 },
        data: {
          label: 'Transferir Atendente',
          message: '👤 Vou transferir você para um atendente humano. Por favor, aguarde...',
          description: 'Opção 1'
        }
      },
      {
        id: 'message-3',
        type: 'message',
        position: { x: 300, y: 580 },
        data: {
          label: 'Ver Produtos',
          message: '🛍️ Aqui está nosso catálogo de produtos:\n\n📦 Produto A - R$ 99,90\n📦 Produto B - R$ 149,90\n📦 Produto C - R$ 199,90',
          description: 'Opção 2'
        }
      },
      {
        id: 'end-1',
        type: 'end',
        position: { x: 100, y: 700 },
        data: { label: 'Fim' }
      }
    ],
    edges: [
      { id: 'e1', source: 'start-1', target: 'message-1', type: 'smoothstep', animated: true },
      { id: 'e2', source: 'message-1', target: 'input-1', type: 'smoothstep' },
      { id: 'e3', source: 'input-1', target: 'condition-1', type: 'smoothstep' },
      { id: 'e4', source: 'condition-1', target: 'message-2', sourceHandle: 'condition-true', type: 'smoothstep', label: 'Opção 1' },
      { id: 'e5', source: 'condition-1', target: 'message-3', sourceHandle: 'condition-false', type: 'smoothstep', label: 'Outras' },
      { id: 'e6', source: 'message-2', target: 'end-1', type: 'smoothstep' },
      { id: 'e7', source: 'message-3', target: 'end-1', type: 'smoothstep' }
    ]
  },
  {
    id: 'support-ticket',
    name: 'Abertura de Ticket de Suporte',
    description: 'Coleta informações e cria ticket de suporte automaticamente',
    category: 'suporte',
    icon: 'headset',
    tags: ['suporte', 'ticket', 'formulário'],
    nodes: [
      {
        id: 'start-1',
        type: 'start',
        position: { x: 250, y: 50 },
        data: { label: 'Início' }
      },
      {
        id: 'message-1',
        type: 'message',
        position: { x: 250, y: 150 },
        data: {
          label: 'Iniciar Suporte',
          message: '🎫 Vou abrir um ticket de suporte para você.\n\nPreciso de algumas informações.',
          description: 'Mensagem inicial'
        }
      },
      {
        id: 'input-1',
        type: 'input',
        position: { x: 250, y: 250 },
        data: {
          label: 'Coletar Nome',
          message: 'Qual é o seu nome completo?',
          inputType: 'text',
          validation: 'required',
          variable: 'nome_cliente',
          description: 'Nome do cliente'
        }
      },
      {
        id: 'input-2',
        type: 'input',
        position: { x: 250, y: 350 },
        data: {
          label: 'Coletar Email',
          message: 'Qual é o seu e-mail?',
          inputType: 'email',
          validation: 'email',
          variable: 'email_cliente',
          description: 'Email para contato'
        }
      },
      {
        id: 'input-3',
        type: 'input',
        position: { x: 250, y: 450 },
        data: {
          label: 'Descrever Problema',
          message: 'Por favor, descreva seu problema com detalhes:',
          inputType: 'text',
          validation: 'required',
          variable: 'descricao_problema',
          description: 'Descrição do problema'
        }
      },
      {
        id: 'action-1',
        type: 'action',
        position: { x: 250, y: 550 },
        data: {
          label: 'Criar Ticket',
          actionType: 'api',
          endpoint: '/api/tickets/create',
          method: 'POST',
          description: 'Envia para sistema de tickets'
        }
      },
      {
        id: 'message-2',
        type: 'message',
        position: { x: 250, y: 650 },
        data: {
          label: 'Confirmação',
          message: '✅ Ticket criado com sucesso!\n\nNúmero do ticket: #{{ticket_id}}\n\nVocê receberá atualizações por e-mail.',
          description: 'Confirmação de ticket'
        }
      },
      {
        id: 'end-1',
        type: 'end',
        position: { x: 250, y: 750 },
        data: { label: 'Fim' }
      }
    ],
    edges: [
      { id: 'e1', source: 'start-1', target: 'message-1', type: 'smoothstep', animated: true },
      { id: 'e2', source: 'message-1', target: 'input-1', type: 'smoothstep' },
      { id: 'e3', source: 'input-1', target: 'input-2', type: 'smoothstep' },
      { id: 'e4', source: 'input-2', target: 'input-3', type: 'smoothstep' },
      { id: 'e5', source: 'input-3', target: 'action-1', type: 'smoothstep' },
      { id: 'e6', source: 'action-1', target: 'message-2', type: 'smoothstep' },
      { id: 'e7', source: 'message-2', target: 'end-1', type: 'smoothstep' }
    ]
  },
  {
    id: 'sales-qualification',
    name: 'Qualificação de Lead de Vendas',
    description: 'Qualifica leads com perguntas e direciona para vendedor adequado',
    category: 'vendas',
    icon: 'chart-line',
    tags: ['vendas', 'lead', 'qualificação'],
    nodes: [
      {
        id: 'start-1',
        type: 'start',
        position: { x: 250, y: 50 },
        data: { label: 'Início' }
      },
      {
        id: 'message-1',
        type: 'message',
        position: { x: 250, y: 150 },
        data: {
          label: 'Saudação',
          message: '💼 Olá! Vejo que você tem interesse em nossos produtos.\n\nVou fazer algumas perguntas rápidas para te direcionar melhor.',
          description: 'Início da qualificação'
        }
      },
      {
        id: 'input-1',
        type: 'input',
        position: { x: 250, y: 270 },
        data: {
          label: 'Empresa',
          message: 'Qual é o nome da sua empresa?',
          inputType: 'text',
          validation: 'required',
          variable: 'nome_empresa',
          description: 'Nome da empresa'
        }
      },
      {
        id: 'input-2',
        type: 'input',
        position: { x: 250, y: 380 },
        data: {
          label: 'Tamanho da Empresa',
          message: 'Quantos funcionários sua empresa tem?\n\n1️⃣ 1-10\n2️⃣ 11-50\n3️⃣ 51-200\n4️⃣ 200+',
          inputType: 'text',
          validation: 'required',
          variable: 'tamanho_empresa',
          description: 'Porte da empresa'
        }
      },
      {
        id: 'condition-1',
        type: 'condition',
        position: { x: 250, y: 500 },
        data: {
          label: 'Qualificar Porte',
          variable: 'tamanho_empresa',
          operator: 'in',
          value: '3,4',
          description: 'Empresa grande?'
        }
      },
      {
        id: 'message-2',
        type: 'message',
        position: { x: 50, y: 620 },
        data: {
          label: 'Vendedor Enterprise',
          message: '🎯 Perfeito! Vou conectar você com nosso especialista em contas corporativas.',
          description: 'Lead qualificado - Enterprise'
        }
      },
      {
        id: 'message-3',
        type: 'message',
        position: { x: 450, y: 620 },
        data: {
          label: 'Vendedor SMB',
          message: '👍 Ótimo! Vou direcionar você para nossa equipe de pequenas e médias empresas.',
          description: 'Lead qualificado - SMB'
        }
      },
      {
        id: 'action-1',
        type: 'action',
        position: { x: 250, y: 740 },
        data: {
          label: 'Salvar Lead',
          actionType: 'database',
          operation: 'insert',
          table: 'leads',
          description: 'Salva no CRM'
        }
      },
      {
        id: 'end-1',
        type: 'end',
        position: { x: 250, y: 850 },
        data: { label: 'Fim' }
      }
    ],
    edges: [
      { id: 'e1', source: 'start-1', target: 'message-1', type: 'smoothstep', animated: true },
      { id: 'e2', source: 'message-1', target: 'input-1', type: 'smoothstep' },
      { id: 'e3', source: 'input-1', target: 'input-2', type: 'smoothstep' },
      { id: 'e4', source: 'input-2', target: 'condition-1', type: 'smoothstep' },
      { id: 'e5', source: 'condition-1', target: 'message-2', sourceHandle: 'condition-true', type: 'smoothstep', label: 'Grande' },
      { id: 'e6', source: 'condition-1', target: 'message-3', sourceHandle: 'condition-false', type: 'smoothstep', label: 'Pequena/Média' },
      { id: 'e7', source: 'message-2', target: 'action-1', type: 'smoothstep' },
      { id: 'e8', source: 'message-3', target: 'action-1', type: 'smoothstep' },
      { id: 'e9', source: 'action-1', target: 'end-1', type: 'smoothstep' }
    ]
  },
  {
    id: 'faq-automation',
    name: 'FAQ Automatizado',
    description: 'Responde perguntas frequentes automaticamente com menu interativo',
    category: 'atendimento',
    icon: 'circle-question',
    tags: ['faq', 'atendimento', 'automação'],
    nodes: [
      {
        id: 'start-1',
        type: 'start',
        position: { x: 300, y: 50 },
        data: { label: 'Início' }
      },
      {
        id: 'message-1',
        type: 'message',
        position: { x: 300, y: 150 },
        data: {
          label: 'Menu FAQ',
          message: '❓ FAQ - Perguntas Frequentes\n\nEscolha uma categoria:\n\n1️⃣ Sobre Produtos\n2️⃣ Formas de Pagamento\n3️⃣ Entrega e Prazos\n4️⃣ Devolução/Troca\n5️⃣ Falar com Atendente',
          description: 'Menu principal FAQ'
        }
      },
      {
        id: 'input-1',
        type: 'input',
        position: { x: 300, y: 300 },
        data: {
          label: 'Escolher Categoria',
          message: 'Digite o número da categoria desejada:',
          inputType: 'text',
          validation: 'required',
          variable: 'categoria_faq',
          description: 'Opção escolhida'
        }
      },
      {
        id: 'condition-1',
        type: 'condition',
        position: { x: 300, y: 420 },
        data: {
          label: 'Produtos?',
          variable: 'categoria_faq',
          operator: 'equals',
          value: '1',
          description: 'Verifica categoria'
        }
      },
      {
        id: 'message-2',
        type: 'message',
        position: { x: 50, y: 560 },
        data: {
          label: 'Info Produtos',
          message: '📦 Sobre Produtos\n\nTrabalhamos com produtos de alta qualidade, garantia de 1 ano e certificação internacional.\n\nTodos os produtos são testados antes do envio.',
          description: 'Resposta sobre produtos'
        }
      },
      {
        id: 'condition-2',
        type: 'condition',
        position: { x: 550, y: 560 },
        data: {
          label: 'Pagamento?',
          variable: 'categoria_faq',
          operator: 'equals',
          value: '2',
          description: 'Verifica pagamento'
        }
      },
      {
        id: 'message-3',
        type: 'message',
        position: { x: 400, y: 700 },
        data: {
          label: 'Info Pagamento',
          message: '💳 Formas de Pagamento\n\n• Cartão de crédito (até 12x)\n• Pix (5% desconto)\n• Boleto bancário\n• Transferência bancária',
          description: 'Resposta sobre pagamento'
        }
      },
      {
        id: 'message-4',
        type: 'message',
        position: { x: 700, y: 700 },
        data: {
          label: 'Transferir Humano',
          message: '👤 Vou transferir você para um atendente humano agora!',
          description: 'Opção 5'
        }
      },
      {
        id: 'end-1',
        type: 'end',
        position: { x: 300, y: 850 },
        data: { label: 'Fim' }
      }
    ],
    edges: [
      { id: 'e1', source: 'start-1', target: 'message-1', type: 'smoothstep', animated: true },
      { id: 'e2', source: 'message-1', target: 'input-1', type: 'smoothstep' },
      { id: 'e3', source: 'input-1', target: 'condition-1', type: 'smoothstep' },
      { id: 'e4', source: 'condition-1', target: 'message-2', sourceHandle: 'condition-true', type: 'smoothstep', label: 'Sim' },
      { id: 'e5', source: 'condition-1', target: 'condition-2', sourceHandle: 'condition-false', type: 'smoothstep', label: 'Não' },
      { id: 'e6', source: 'condition-2', target: 'message-3', sourceHandle: 'condition-true', type: 'smoothstep', label: 'Sim' },
      { id: 'e7', source: 'condition-2', target: 'message-4', sourceHandle: 'condition-false', type: 'smoothstep', label: 'Outro' },
      { id: 'e8', source: 'message-2', target: 'end-1', type: 'smoothstep' },
      { id: 'e9', source: 'message-3', target: 'end-1', type: 'smoothstep' },
      { id: 'e10', source: 'message-4', target: 'end-1', type: 'smoothstep' }
    ]
  },
  // =============================================
  // TEMPLATE: CHECKLIST COMPLETO DE VEÍCULOS v2.0
  // Sistema de Grupos de Itens com Conferência
  // MELHORIAS: Botões interativos, validação, timeout, GPS, resumo por grupo
  // =============================================
  {
    id: 'checklist-whatsapp-automation',
    name: 'Checklist Completo de Veículo',
    description: 'Automação completa de checklist com grupos de itens, conferência individual, geração de ocorrências e notificação ao gestor. Inclui: botões interativos, validação de respostas, timeout, GPS e resumo por grupo.',
    category: 'frota',
    icon: 'truck',
    tags: ['checklist', 'veículo', 'whatsapp', 'frota', 'automação', 'ocorrências', 'botões', 'GPS'],
    nodes: [
      // =============================================
      // INÍCIO - Recebe dados do WhatsApp
      // =============================================
      {
        id: 'start-1',
        type: 'start',
        position: { x: 400, y: 0 },
        data: {
          label: 'Início',
          description: 'Recebe mensagem do WhatsApp',
          inputParameters: [
            { name: 'phone_number', type: 'string', description: 'Número do telefone do remetente' },
            { name: 'message_text', type: 'string', description: 'Texto da mensagem recebida' },
            { name: 'message_type', type: 'string', description: 'Tipo: text, image, audio, document, location' },
            { name: 'message_media_url', type: 'string', description: 'URL da mídia (se houver)' },
            { name: 'message_id', type: 'string', description: 'ID único da mensagem' },
            { name: 'is_group', type: 'boolean', description: 'Se é mensagem de grupo' },
            { name: 'group_id', type: 'string', description: 'ID do grupo (se for grupo)' },
            { name: 'contact_name', type: 'string', description: 'Nome do contato' },
            { name: 'timestamp', type: 'string', description: 'Data/hora da mensagem' },
            { name: 'empresa_id', type: 'number', description: 'ID da empresa' },
            { name: 'latitude', type: 'number', description: 'Latitude (se localização)' },
            { name: 'longitude', type: 'number', description: 'Longitude (se localização)' }
          ]
        }
      },
      // =============================================
      // DEFINIR VARIÁVEIS INICIAIS
      // =============================================
      {
        id: 'set-vars-inicial',
        type: 'set_variables',
        position: { x: 400, y: 100 },
        data: {
          label: 'Variáveis Iniciais',
          assignments: [
            { id: 'v1', name: 'itens_com_problema', value: '[]', type: 'array' },
            { id: 'v2', name: 'total_itens_checados', value: '0', type: 'number' },
            { id: 'v3', name: 'total_problemas', value: '0', type: 'number' },
            { id: 'v4', name: 'grupo_atual', value: '0', type: 'number' },
            { id: 'v5', name: 'item_atual', value: '0', type: 'number' },
            { id: 'v6', name: 'checklist_iniciado', value: 'false', type: 'boolean' },
            { id: 'v7', name: 'itens_ok_grupo', value: '0', type: 'number' },
            { id: 'v8', name: 'itens_nok_grupo', value: '0', type: 'number' },
            { id: 'v9', name: 'tentativas_resposta', value: '0', type: 'number' },
            { id: 'v10', name: 'max_tentativas', value: '3', type: 'number' },
            { id: 'v11', name: 'observacoes_gerais', value: '', type: 'string' },
            { id: 'v12', name: 'total_grupos', value: '0', type: 'number' },
            { id: 'v13', name: 'grupo_index', value: '0', type: 'number' },
            { id: 'v14', name: 'item_index', value: '0', type: 'number' },
            { id: 'v15', name: 'foto_item_url', value: '', type: 'string' },
            { id: 'v16', name: 'descricao_problema', value: '', type: 'string' },
            { id: 'v17', name: 'status_checklist', value: 'em_andamento', type: 'string' },
            { id: 'v18', name: 'observacoes_final', value: '', type: 'string' }
          ],
          description: 'Inicializa contadores e arrays para o checklist'
        }
      },
      // =============================================
      // SOLICITAR LOCALIZAÇÃO GPS
      // =============================================
      {
        id: 'message-solicitar-gps',
        type: 'message',
        position: { x: 400, y: 200 },
        data: {
          label: 'Solicitar GPS',
          message: '*CHECKLIST DE VEÍCULO*\n\nPara iniciar, compartilhe sua *localização atual*.\n\nClique no ícone de anexo (📎) e selecione "Localização".',
          messageType: 'text'
        }
      },
      {
        id: 'input-gps',
        type: 'input',
        position: { x: 400, y: 300 },
        data: {
          label: 'Capturar GPS',
          inputType: 'location',
          variableName: 'localizacao_inicial',
          description: 'Captura localização GPS do motorista',
          timeout: 120000,
          timeoutMessage: 'Tempo esgotado aguardando localização. Por favor, compartilhe sua localização para continuar.'
        }
      },
      {
        id: 'action-salvar-gps',
        type: 'action',
        position: { x: 400, y: 400 },
        data: {
          label: 'Salvar GPS',
          description: 'Armazena coordenadas GPS',
          actions: {
            output: [
              {
                id: 'store_latitude',
                type: 'store_variable',
                label: 'Salvar Latitude',
                enabled: true,
                order: 1,
                config: {
                  variableName: 'latitude_inicial',
                  value: '{{latitude}}',
                  scope: 'flow'
                }
              },
              {
                id: 'store_longitude',
                type: 'store_variable',
                label: 'Salvar Longitude',
                enabled: true,
                order: 2,
                config: {
                  variableName: 'longitude_inicial',
                  value: '{{longitude}}',
                  scope: 'flow'
                }
              }
            ]
          }
        }
      },
      {
        id: 'message-gps-ok',
        type: 'message',
        position: { x: 400, y: 500 },
        data: {
          label: 'GPS Recebido',
          message: '📍 Localização registrada!\n\nAgora informe a *PLACA* do veículo:',
          messageType: 'text'
        }
      },
      {
        id: 'input-placa',
        type: 'input',
        position: { x: 400, y: 600 },
        data: {
          label: 'Capturar Placa',
          inputType: 'text',
          variableName: 'placa_informada',
          description: 'Captura a placa do veículo',
          timeout: 60000,
          timeoutMessage: 'Tempo esgotado. Por favor, informe a placa do veículo.'
        }
      },
      // =============================================
      // VERIFICAR SE É MENSAGEM DE GRUPO
      // =============================================
      {
        id: 'condition-grupo',
        type: 'condition',
        position: { x: 400, y: 700 },
        data: {
          label: 'É Grupo?',
          variable: 'is_group',
          operator: 'equals',
          value: 'true',
          description: 'Ignora mensagens de grupos'
        }
      },
      {
        id: 'message-grupo-ignorado',
        type: 'message',
        position: { x: 700, y: 700 },
        data: {
          label: 'Grupo Ignorado',
          message: 'Mensagens de grupos não são processadas. Envie no privado.',
          messageType: 'text'
        }
      },
      // =============================================
      // BUSCAR VEÍCULO PELA PLACA
      // =============================================
      {
        id: 'action-buscar-veiculo',
        type: 'action',
        position: { x: 400, y: 800 },
        data: {
          label: 'Buscar Veículo',
          description: 'Busca veículo pela placa informada',
          actions: {
            input: [
              {
                id: 'transform_placa',
                type: 'transform_uppercase',
                label: 'Converter para Maiúsculo',
                enabled: true,
                order: 1,
                config: { field: 'placa_informada' }
              }
            ],
            output: [
              {
                id: 'api_buscar_veiculo',
                type: 'api_call',
                label: 'Buscar Veículo (API)',
                enabled: true,
                order: 1,
                config: {
                  url: '/api/veiculos/placa/{{placa_informada}}',
                  method: 'GET',
                  headers: '{"Content-Type": "application/json"}',
                  targetVariable: 'veiculo_response',
                  timeout: 10000,
                  retryOnError: true,
                  maxRetries: 2
                }
              },
              {
                id: 'extract_veiculo',
                type: 'extract_field',
                label: 'Extrair Veículo',
                enabled: true,
                order: 2,
                config: {
                  sourcePath: 'veiculo_response.data',
                  targetVariable: 'veiculo',
                  defaultValue: null
                }
              },
              {
                id: 'check_api_error',
                type: 'extract_field',
                label: 'Verificar Erro API',
                enabled: true,
                order: 3,
                config: {
                  sourcePath: 'veiculo_response.success',
                  targetVariable: 'api_success',
                  defaultValue: false
                }
              }
            ]
          }
        }
      },
      // =============================================
      // VERIFICAR ERRO NA API
      // =============================================
      {
        id: 'condition-api-error',
        type: 'condition',
        position: { x: 400, y: 900 },
        data: {
          label: 'API OK?',
          variable: 'api_success',
          operator: 'equals',
          value: 'true',
          description: 'Verifica se a API respondeu sem erro'
        }
      },
      {
        id: 'message-api-error',
        type: 'message',
        position: { x: 700, y: 850 },
        data: {
          label: 'Erro na API',
          message: 'Desculpe, ocorreu um erro ao buscar o veículo. Tente novamente em alguns instantes.',
          messageType: 'text'
        }
      },
      // =============================================
      // VERIFICAR SE VEÍCULO EXISTE
      // =============================================
      {
        id: 'condition-veiculo-existe',
        type: 'condition',
        position: { x: 400, y: 1000 },
        data: {
          label: 'Veículo Existe?',
          variable: 'veiculo',
          operator: 'not_null',
          value: '',
          description: 'Verifica se encontrou o veículo'
        }
      },
      {
        id: 'message-veiculo-nao-encontrado',
        type: 'message',
        position: { x: 1000, y: 900 },
        data: {
          label: 'Veículo Não Encontrado',
          message: 'Veículo com placa {{placa_informada}} não encontrado.\n\nVerifique a placa e tente novamente.',
          messageType: 'text'
        }
      },
      // =============================================
      // BUSCAR MOTORISTA PELO CELULAR
      // =============================================
      {
        id: 'action-buscar-motorista',
        type: 'action',
        position: { x: 400, y: 1050 },
        data: {
          label: 'Buscar Motorista',
          description: 'Busca motorista pelo número de celular (WhatsApp)',
          actions: {
            input: [
              {
                id: 'format_phone',
                type: 'store_variable',
                label: 'Formatar Telefone',
                enabled: true,
                order: 1,
                config: {
                  variableName: 'phone_formatted',
                  value: '{{phone_number}}'
                }
              }
            ],
            output: [
              {
                id: 'api_buscar_motorista',
                type: 'api_call',
                label: 'Buscar Motorista (API)',
                enabled: true,
                order: 1,
                config: {
                  url: '/api/motoristas/celular/{{phone_number}}',
                  method: 'GET',
                  headers: '{"Content-Type": "application/json"}',
                  targetVariable: 'motorista_response',
                  timeout: 10000,
                  retryOnError: true,
                  maxRetries: 2
                }
              },
              {
                id: 'extract_motorista',
                type: 'extract_field',
                label: 'Extrair Motorista',
                enabled: true,
                order: 2,
                config: {
                  sourcePath: 'motorista_response.data',
                  targetVariable: 'motorista',
                  defaultValue: null
                }
              },
              {
                id: 'set_motorista_fallback',
                type: 'condition_check',
                label: 'Verificar Motorista',
                enabled: true,
                order: 3,
                config: {
                  condition: {
                    variable: 'motorista',
                    operator: 'is_null',
                    value: ''
                  },
                  onTrue: {
                    type: 'store_variable',
                    config: {
                      variableName: 'motorista',
                      value: JSON.stringify({
                        id: null,
                        nome: '{{contact_name}}',
                        celular: '{{phone_number}}'
                      })
                    }
                  }
                }
              }
            ]
          }
        }
      },
      // =============================================
      // CRIAR CHECKLIST
      // =============================================
      {
        id: 'action-criar-checklist',
        type: 'checklist',
        position: { x: 400, y: 1150 },
        data: {
          label: 'Criar Checklist',
          operation: 'create',
          description: 'Cria novo checklist para o veículo',
          actions: {
            output: [
              {
                id: 'api_criar_checklist',
                type: 'api_call',
                label: 'Criar Checklist (API)',
                enabled: true,
                order: 1,
                config: {
                  url: '/api/checklists',
                  method: 'POST',
                  headers: '{"Content-Type": "application/json"}',
                  body: JSON.stringify({
                    veiculo_id: '{{veiculo.id}}',
                    motorista_id: '{{motorista.id}}',
                    tipo: 'diario',
                    status: 'em_andamento',
                    empresa_id: '{{empresa_id}}'
                  }),
                  targetVariable: 'checklist_response'
                }
              },
              {
                id: 'extract_checklist_id',
                type: 'extract_field',
                label: 'Extrair ID do Checklist',
                enabled: true,
                order: 2,
                config: {
                  sourcePath: 'checklist_response.data.id',
                  targetVariable: 'checklist_id'
                }
              }
            ]
          }
        }
      },
      // =============================================
      // BUSCAR GRUPOS DE CHECKLIST
      // =============================================
      {
        id: 'action-buscar-grupos',
        type: 'action',
        position: { x: 400, y: 1200 },
        data: {
          label: 'Buscar Grupos',
          description: 'Busca grupos de checklist configurados',
          actions: {
            output: [
              {
                id: 'api_buscar_grupos',
                type: 'api_call',
                label: 'Buscar Grupos (API)',
                enabled: true,
                order: 1,
                config: {
                  url: '/api/grupos-checklist?status=Ativo&empresa_id={{empresa_id}}',
                  method: 'GET',
                  headers: '{"Content-Type": "application/json"}',
                  targetVariable: 'grupos_response'
                }
              },
              {
                id: 'extract_grupos',
                type: 'extract_field',
                label: 'Extrair Grupos',
                enabled: true,
                order: 2,
                config: {
                  sourcePath: 'grupos_response.data',
                  targetVariable: 'grupos_checklist',
                  defaultValue: []
                }
              },
              {
                id: 'store_total_grupos',
                type: 'store_variable',
                label: 'Total de Grupos',
                enabled: true,
                order: 3,
                config: {
                  variableName: 'total_grupos',
                  value: '{{grupos_checklist.length}}',
                  scope: 'flow'
                }
              }
            ]
          }
        }
      },
      // =============================================
      // SOLICITAR KM INICIAL
      // =============================================
      {
        id: 'message-solicitar-km',
        type: 'message',
        position: { x: 400, y: 1300 },
        data: {
          label: 'Solicitar KM',
          message: '*CHECKLIST - {{veiculo.placa}}*\n{{veiculo.modelo}} - {{veiculo.cor}}\n\nInforme o *KM ATUAL* do veículo:',
          messageType: 'text'
        }
      },
      {
        id: 'input-km-inicial',
        type: 'input',
        position: { x: 400, y: 1400 },
        data: {
          label: 'Capturar KM',
          inputType: 'number',
          variableName: 'km_atual',
          validationType: 'integer',
          validationMessage: 'Digite apenas números para o KM.',
          description: 'Captura o KM atual do veículo'
        }
      },
      // =============================================
      // SALVAR KM E SOLICITAR FOTO
      // =============================================
      {
        id: 'action-salvar-km',
        type: 'action',
        position: { x: 400, y: 1500 },
        data: {
          label: 'Salvar KM',
          description: 'Salva o KM no checklist',
          actions: {
            output: [
              {
                id: 'api_atualizar_km',
                type: 'api_call',
                label: 'Atualizar KM (API)',
                enabled: true,
                order: 1,
                config: {
                  url: '/api/checklists/{{checklist_id}}',
                  method: 'PUT',
                  headers: '{"Content-Type": "application/json"}',
                  body: '{"odometro": {{km_atual}}}',
                  targetVariable: 'update_km_response'
                }
              }
            ]
          }
        }
      },
      {
        id: 'message-solicitar-foto-km',
        type: 'message',
        position: { x: 400, y: 1600 },
        data: {
          label: 'Solicitar Foto KM',
          message: 'Agora envie uma *FOTO DO HODOMETRO* mostrando o KM informado:',
          messageType: 'text'
        }
      },
      {
        id: 'input-foto-km',
        type: 'input',
        position: { x: 400, y: 1700 },
        data: {
          label: 'Capturar Foto KM',
          inputType: 'image',
          variableName: 'foto_hodometro',
          description: 'Captura foto do hodômetro'
        }
      },
      {
        id: 'action-salvar-foto-km',
        type: 'action',
        position: { x: 400, y: 1800 },
        data: {
          label: 'Salvar Foto KM',
          description: 'Salva a foto do hodômetro',
          actions: {
            output: [
              {
                id: 'api_salvar_foto_km',
                type: 'api_call',
                label: 'Salvar Foto KM (API)',
                enabled: true,
                order: 1,
                config: {
                  url: '/api/checklists/{{checklist_id}}',
                  method: 'PUT',
                  headers: '{"Content-Type": "application/json"}',
                  body: '{"fotos": {"hodometro": "{{message_media_url}}"}}',
                  targetVariable: 'save_foto_response'
                }
              }
            ]
          }
        }
      },
      // =============================================
      // LOOP: PROCESSAR GRUPOS DE CHECKLIST
      // =============================================
      {
        id: 'loop-grupos',
        type: 'loop',
        position: { x: 400, y: 1900 },
        data: {
          label: 'Loop Grupos',
          loopType: 'forEach',
          collection: 'grupos_checklist',
          itemVariable: 'grupo_atual_obj',
          indexVariable: 'grupo_index',
          description: 'Itera sobre cada grupo de checklist'
        }
      },
      // =============================================
      // ENVIAR MENSAGEM DO GRUPO
      // =============================================
      {
        id: 'message-grupo-checklist',
        type: 'message',
        position: { x: 400, y: 2000 },
        data: {
          label: 'Mensagem Grupo',
          message: '*GRUPO {{grupo_index + 1}}/{{total_grupos}}: {{grupo_atual_obj.nome}}*\n\n{{grupo_atual_obj.descricao}}\n\nVamos verificar os itens deste grupo:',
          messageType: 'text'
        }
      },
      // =============================================
      // BUSCAR ITENS DO GRUPO
      // =============================================
      {
        id: 'action-buscar-itens-grupo',
        type: 'action',
        position: { x: 400, y: 2100 },
        data: {
          label: 'Buscar Itens',
          description: 'Busca itens do grupo atual',
          actions: {
            output: [
              {
                id: 'api_buscar_itens',
                type: 'api_call',
                label: 'Buscar Itens do Grupo (API)',
                enabled: true,
                order: 1,
                config: {
                  url: '/api/grupos-checklist/{{grupo_atual_obj.id}}/itens',
                  method: 'GET',
                  headers: '{"Content-Type": "application/json"}',
                  targetVariable: 'itens_grupo_response'
                }
              },
              {
                id: 'extract_itens',
                type: 'extract_field',
                label: 'Extrair Itens',
                enabled: true,
                order: 2,
                config: {
                  sourcePath: 'itens_grupo_response.data',
                  targetVariable: 'itens_grupo',
                  defaultValue: []
                }
              },
              {
                id: 'store_total_itens_grupo',
                type: 'store_variable',
                label: 'Total Itens Grupo',
                enabled: true,
                order: 3,
                config: {
                  variableName: 'total_itens_grupo',
                  value: '{{itens_grupo.length}}',
                  scope: 'flow'
                }
              }
            ]
          }
        }
      },
      // =============================================
      // LOOP: PROCESSAR ITENS DO GRUPO
      // =============================================
      {
        id: 'loop-itens',
        type: 'loop',
        position: { x: 400, y: 2200 },
        data: {
          label: 'Loop Itens',
          loopType: 'forEach',
          collection: 'itens_grupo',
          itemVariable: 'item_atual_obj',
          indexVariable: 'item_index',
          description: 'Itera sobre cada item do grupo'
        }
      },
      // =============================================
      // RESETAR TENTATIVAS DE RESPOSTA
      // =============================================
      {
        id: 'action-reset-tentativas',
        type: 'action',
        position: { x: 400, y: 2300 },
        data: {
          label: 'Resetar Tentativas',
          description: 'Reseta contador de tentativas para cada item',
          actions: {
            output: [
              {
                id: 'reset_tentativas',
                type: 'store_variable',
                label: 'Resetar Tentativas',
                enabled: true,
                order: 1,
                config: {
                  variableName: 'tentativas_resposta',
                  value: '0',
                  scope: 'flow'
                }
              }
            ]
          }
        }
      },
      // =============================================
      // PERGUNTAR STATUS DO ITEM (OPÇÕES NUMÉRICAS)
      // =============================================
      {
        id: 'message-item-checklist',
        type: 'message',
        position: { x: 400, y: 2400 },
        data: {
          label: 'Perguntar Item',
          message: '*Item {{item_index + 1}}/{{total_itens_grupo}}:*\n{{item_atual_obj.descricao}}\n\nResponda:\n*1* - OK (conforme)\n*2* - NOK (problema encontrado)',
          messageType: 'text',
          description: 'Pergunta com opções numéricas'
        }
      },
      {
        id: 'input-resposta-item',
        type: 'input',
        position: { x: 400, y: 2500 },
        data: {
          label: 'Capturar Resposta',
          inputType: 'text',
          variableName: 'resposta_item',
          description: 'Captura a resposta do motorista (1=OK, 2=NOK)',
          timeout: 120000,
          timeoutMessage: 'Tempo esgotado aguardando resposta. Por favor, digite 1 para OK ou 2 para NOK.'
        }
      },
      // =============================================
      // VALIDAR RESPOSTA (1 OU 2)
      // =============================================
      {
        id: 'condition-resposta-valida',
        type: 'condition',
        position: { x: 400, y: 2600 },
        data: {
          label: 'Resposta Válida?',
          variable: 'resposta_item',
          operator: 'in',
          value: '1,2',
          description: 'Verifica se a resposta é 1 ou 2'
        }
      },
      {
        id: 'action-incrementar-tentativas',
        type: 'action',
        position: { x: 700, y: 2600 },
        data: {
          label: 'Incrementar Tentativas',
          description: 'Incrementa contador de tentativas inválidas',
          actions: {
            output: [
              {
                id: 'inc_tentativas',
                type: 'store_variable',
                label: 'Incrementar Tentativas',
                enabled: true,
                order: 1,
                config: {
                  variableName: 'tentativas_resposta',
                  value: '{{tentativas_resposta + 1}}',
                  scope: 'flow'
                }
              }
            ]
          }
        }
      },
      {
        id: 'condition-max-tentativas',
        type: 'condition',
        position: { x: 700, y: 2700 },
        data: {
          label: 'Excedeu Tentativas?',
          variable: 'tentativas_resposta',
          operator: 'greater_than_or_equal',
          value: '{{max_tentativas}}',
          description: 'Verifica se excedeu o máximo de tentativas'
        }
      },
      {
        id: 'message-resposta-invalida',
        type: 'message',
        position: { x: 950, y: 2600 },
        data: {
          label: 'Resposta Inválida',
          message: 'Resposta inválida. Por favor, digite:\n*1* - OK (conforme)\n*2* - NOK (problema encontrado)',
          messageType: 'text'
        }
      },
      {
        id: 'message-assumir-ok',
        type: 'message',
        position: { x: 950, y: 2800 },
        data: {
          label: 'Assumir OK',
          message: 'Máximo de tentativas excedido. Assumindo item como *OK*.',
          messageType: 'text'
        }
      },
      {
        id: 'action-forcar-ok',
        type: 'action',
        position: { x: 950, y: 2900 },
        data: {
          label: 'Forçar OK',
          description: 'Define resposta como OK após exceder tentativas',
          actions: {
            output: [
              {
                id: 'set_resposta_ok',
                type: 'store_variable',
                label: 'Definir como OK',
                enabled: true,
                order: 1,
                config: {
                  variableName: 'resposta_item',
                  value: '1',
                  scope: 'flow'
                }
              }
            ]
          }
        }
      },
      // =============================================
      // VERIFICAR SE É OK OU NOK
      // =============================================
      {
        id: 'condition-item-ok',
        type: 'condition',
        position: { x: 400, y: 3000 },
        data: {
          label: 'Item OK?',
          variable: 'resposta_item',
          operator: 'equals',
          value: '1',
          description: 'Verifica se o item está conforme'
        }
      },
      // =============================================
      // FLUXO OK: SALVAR ITEM CONFORME
      // =============================================
      {
        id: 'action-salvar-item-ok',
        type: 'action',
        position: { x: 150, y: 3100 },
        data: {
          label: 'Salvar Item OK',
          description: 'Salva item como conforme',
          actions: {
            output: [
              {
                id: 'api_salvar_item_ok',
                type: 'api_call',
                label: 'Salvar Item (API)',
                enabled: true,
                order: 1,
                config: {
                  url: '/api/itens-checklist',
                  method: 'POST',
                  headers: '{"Content-Type": "application/json"}',
                  body: JSON.stringify({
                    checklist_id: '{{checklist_id}}',
                    empresa_id: '{{empresa_id}}',
                    categoria: '{{grupo_atual_obj.nome}}',
                    descricao: '{{item_atual_obj.descricao}}',
                    tipo: 'booleano',
                    valor: 'ok',
                    obrigatorio: '{{item_atual_obj.obrigatorio}}',
                    ordem: '{{item_index}}'
                  }),
                  targetVariable: 'save_item_ok_response'
                }
              },
              {
                id: 'incrementar_checados',
                type: 'store_variable',
                label: 'Incrementar Checados',
                enabled: true,
                order: 2,
                config: {
                  variableName: 'total_itens_checados',
                  value: '{{total_itens_checados + 1}}',
                  scope: 'flow'
                }
              },
              {
                id: 'incrementar_ok_grupo',
                type: 'store_variable',
                label: 'Incrementar OK Grupo',
                enabled: true,
                order: 3,
                config: {
                  variableName: 'itens_ok_grupo',
                  value: '{{itens_ok_grupo + 1}}',
                  scope: 'flow'
                }
              }
            ]
          }
        }
      },
      // =============================================
      // FLUXO NOK: SOLICITAR DETALHES DO PROBLEMA
      // =============================================
      {
        id: 'message-solicitar-descricao-problema',
        type: 'message',
        position: { x: 650, y: 3100 },
        data: {
          label: 'Solicitar Descrição',
          message: 'Descreva o problema encontrado em *{{item_atual_obj.descricao}}*:',
          messageType: 'text'
        }
      },
      {
        id: 'input-descricao-problema',
        type: 'input',
        position: { x: 650, y: 3200 },
        data: {
          label: 'Capturar Descrição',
          inputType: 'text',
          variableName: 'descricao_problema',
          description: 'Captura descrição do problema'
        }
      },
      {
        id: 'message-solicitar-foto-problema',
        type: 'message',
        position: { x: 650, y: 3300 },
        data: {
          label: 'Solicitar Foto',
          message: 'Agora envie uma *FOTO* do problema:',
          messageType: 'text'
        }
      },
      {
        id: 'input-foto-problema',
        type: 'input',
        position: { x: 650, y: 3400 },
        data: {
          label: 'Capturar Foto Problema',
          inputType: 'image',
          variableName: 'foto_problema',
          description: 'Captura foto do problema'
        }
      },
      // =============================================
      // SALVAR ITEM COM PROBLEMA
      // =============================================
      {
        id: 'action-salvar-item-nok',
        type: 'action',
        position: { x: 650, y: 3500 },
        data: {
          label: 'Salvar Item NOK',
          description: 'Salva item com problema e foto',
          actions: {
            output: [
              {
                id: 'api_salvar_item_nok',
                type: 'api_call',
                label: 'Salvar Item (API)',
                enabled: true,
                order: 1,
                config: {
                  url: '/api/itens-checklist',
                  method: 'POST',
                  headers: '{"Content-Type": "application/json"}',
                  body: JSON.stringify({
                    checklist_id: '{{checklist_id}}',
                    empresa_id: '{{empresa_id}}',
                    categoria: '{{grupo_atual_obj.nome}}',
                    descricao: '{{item_atual_obj.descricao}}',
                    tipo: 'booleano',
                    valor: 'nok',
                    observacao: '{{descricao_problema}}',
                    fotos: ['{{message_media_url}}'],
                    obrigatorio: '{{item_atual_obj.obrigatorio}}',
                    ordem: '{{item_index}}'
                  }),
                  targetVariable: 'save_item_nok_response'
                }
              },
              {
                id: 'extract_item_id',
                type: 'extract_field',
                label: 'Extrair ID do Item',
                enabled: true,
                order: 2,
                config: {
                  sourcePath: 'save_item_nok_response.data.id',
                  targetVariable: 'item_problema_id'
                }
              }
            ]
          }
        }
      },
      // =============================================
      // CRIAR OCORRÊNCIA PARA O PROBLEMA
      // =============================================
      {
        id: 'action-criar-ocorrencia',
        type: 'action',
        position: { x: 650, y: 3600 },
        data: {
          label: 'Criar Ocorrência',
          description: 'Cria ocorrência para o problema encontrado',
          actions: {
            output: [
              {
                id: 'api_criar_ocorrencia',
                type: 'api_call',
                label: 'Criar Ocorrência (API)',
                enabled: true,
                order: 1,
                config: {
                  url: '/api/ocorrencias',
                  method: 'POST',
                  headers: '{"Content-Type": "application/json"}',
                  body: JSON.stringify({
                    tipo: 'checklist',
                    titulo: 'Problema em {{item_atual_obj.descricao}}',
                    descricao: '{{descricao_problema}}',
                    veiculo_id: '{{veiculo.id}}',
                    checklist_id: '{{checklist_id}}',
                    checklist_item_id: '{{item_problema_id}}',
                    prioridade: 'media',
                    status: 'aberta',
                    fotos: ['{{message_media_url}}'],
                    empresa_id: '{{empresa_id}}',
                    reportado_por: '{{motorista.id}}'
                  }),
                  targetVariable: 'ocorrencia_response'
                }
              },
              {
                id: 'extract_ocorrencia_id',
                type: 'extract_field',
                label: 'Extrair ID Ocorrência',
                enabled: true,
                order: 2,
                config: {
                  sourcePath: 'ocorrencia_response.data.id',
                  targetVariable: 'ocorrencia_id'
                }
              }
            ]
          }
        }
      },
      // =============================================
      // ADICIONAR À LISTA DE PROBLEMAS
      // =============================================
      {
        id: 'action-adicionar-problema-lista',
        type: 'action',
        position: { x: 650, y: 3700 },
        data: {
          label: 'Adicionar à Lista',
          description: 'Adiciona problema à lista para o e-mail',
          actions: {
            output: [
              {
                id: 'adicionar_problema',
                type: 'array_push',
                label: 'Adicionar Problema',
                enabled: true,
                order: 1,
                config: {
                  arrayVariable: 'itens_com_problema',
                  item: {
                    grupo: '{{grupo_atual_obj.nome}}',
                    item: '{{item_atual_obj.descricao}}',
                    descricao: '{{descricao_problema}}',
                    foto: '{{message_media_url}}',
                    ocorrencia_id: '{{ocorrencia_id}}'
                  }
                }
              },
              {
                id: 'incrementar_problemas',
                type: 'store_variable',
                label: 'Incrementar Problemas',
                enabled: true,
                order: 2,
                config: {
                  variableName: 'total_problemas',
                  value: '{{total_problemas + 1}}',
                  scope: 'flow'
                }
              },
              {
                id: 'incrementar_checados_nok',
                type: 'store_variable',
                label: 'Incrementar Checados',
                enabled: true,
                order: 3,
                config: {
                  variableName: 'total_itens_checados',
                  value: '{{total_itens_checados + 1}}',
                  scope: 'flow'
                }
              },
              {
                id: 'incrementar_nok_grupo',
                type: 'store_variable',
                label: 'Incrementar NOK Grupo',
                enabled: true,
                order: 4,
                config: {
                  variableName: 'itens_nok_grupo',
                  value: '{{itens_nok_grupo + 1}}',
                  scope: 'flow'
                }
              }
            ]
          }
        }
      },
      {
        id: 'message-ocorrencia-criada',
        type: 'message',
        position: { x: 650, y: 3800 },
        data: {
          label: 'Confirmar Ocorrência',
          message: 'Ocorrencia #{{ocorrencia_id}} criada para este problema.',
          messageType: 'text'
        }
      },
      // =============================================
      // JUNÇÃO DOS FLUXOS OK/NOK
      // =============================================
      {
        id: 'merge-item',
        type: 'merge',
        position: { x: 400, y: 3900 },
        data: {
          label: 'Continuar',
          description: 'Junta os fluxos OK e NOK'
        }
      },
      // =============================================
      // FIM DO LOOP DE ITENS
      // =============================================
      {
        id: 'loop-itens-end',
        type: 'loop_end',
        position: { x: 400, y: 4000 },
        data: {
          label: 'Fim Loop Itens',
          loopId: 'loop-itens',
          description: 'Volta para o próximo item ou sai do loop'
        }
      },
      // =============================================
      // MENSAGEM FIM DO GRUPO COM RESUMO
      // =============================================
      {
        id: 'message-fim-grupo',
        type: 'message',
        position: { x: 400, y: 4100 },
        data: {
          label: 'Fim do Grupo',
          message: '*GRUPO {{grupo_atual_obj.nome}} - CONCLUÍDO*\n\n📊 *Resumo:*\n✅ OK: {{itens_ok_grupo}} itens\n❌ NOK: {{itens_nok_grupo}} itens',
          messageType: 'text'
        }
      },
      {
        id: 'action-reset-contadores-grupo',
        type: 'action',
        position: { x: 400, y: 4200 },
        data: {
          label: 'Resetar Contadores',
          description: 'Reseta contadores para próximo grupo',
          actions: {
            output: [
              {
                id: 'reset_ok_grupo',
                type: 'store_variable',
                label: 'Resetar OK Grupo',
                enabled: true,
                order: 1,
                config: {
                  variableName: 'itens_ok_grupo',
                  value: '0',
                  scope: 'flow'
                }
              },
              {
                id: 'reset_nok_grupo',
                type: 'store_variable',
                label: 'Resetar NOK Grupo',
                enabled: true,
                order: 2,
                config: {
                  variableName: 'itens_nok_grupo',
                  value: '0',
                  scope: 'flow'
                }
              }
            ]
          }
        }
      },
      // =============================================
      // FIM DO LOOP DE GRUPOS
      // =============================================
      {
        id: 'loop-grupos-end',
        type: 'loop_end',
        position: { x: 400, y: 4300 },
        data: {
          label: 'Fim Loop Grupos',
          loopId: 'loop-grupos',
          description: 'Volta para o próximo grupo ou sai do loop'
        }
      },
      // =============================================
      // SOLICITAR OBSERVAÇÕES GERAIS
      // =============================================
      {
        id: 'message-solicitar-observacoes',
        type: 'message',
        position: { x: 400, y: 4400 },
        data: {
          label: 'Solicitar Observações',
          message: '*OBSERVAÇÕES GERAIS*\n\nDigite alguma observação adicional ou envie *N* para nenhuma:',
          messageType: 'text'
        }
      },
      {
        id: 'input-observacoes',
        type: 'input',
        position: { x: 400, y: 4500 },
        data: {
          label: 'Capturar Observações',
          inputType: 'text',
          variableName: 'observacoes_gerais',
          description: 'Captura observações gerais'
        }
      },
      // =============================================
      // FINALIZAR CHECKLIST
      // =============================================
      {
        id: 'action-finalizar-checklist',
        type: 'checklist',
        position: { x: 400, y: 4600 },
        data: {
          label: 'Finalizar Checklist',
          operation: 'complete',
          description: 'Marca checklist como concluído',
          actions: {
            input: [
              {
                id: 'calcular_status',
                type: 'condition_check',
                label: 'Calcular Status',
                enabled: true,
                order: 1,
                config: {
                  condition: {
                    variable: 'total_problemas',
                    operator: 'greater_than',
                    value: '0'
                  },
                  onTrue: {
                    type: 'store_variable',
                    config: { variableName: 'status_checklist', value: 'reprovado' }
                  },
                  onFalse: {
                    type: 'store_variable',
                    config: { variableName: 'status_checklist', value: 'concluido' }
                  }
                }
              },
              {
                id: 'preparar_observacao',
                type: 'condition_check',
                label: 'Preparar Observação',
                enabled: true,
                order: 2,
                config: {
                  condition: {
                    variable: 'observacoes_gerais',
                    operator: 'in',
                    value: 'N,n'
                  },
                  onTrue: {
                    type: 'store_variable',
                    config: { variableName: 'observacoes_final', value: '' }
                  },
                  onFalse: {
                    type: 'store_variable',
                    config: { variableName: 'observacoes_final', value: '{{observacoes_gerais}}' }
                  }
                }
              }
            ],
            output: [
              {
                id: 'api_finalizar_checklist',
                type: 'api_call',
                label: 'Finalizar Checklist (API)',
                enabled: true,
                order: 1,
                config: {
                  url: '/api/checklists/{{checklist_id}}',
                  method: 'PUT',
                  headers: '{"Content-Type": "application/json"}',
                  body: JSON.stringify({
                    status: '{{status_checklist}}',
                    observacoes: '{{observacoes_final}}'
                  }),
                  targetVariable: 'finalize_response'
                }
              }
            ]
          }
        }
      },
      // =============================================
      // MENSAGEM DE CONCLUSÃO
      // =============================================
      {
        id: 'message-checklist-concluido',
        type: 'message',
        position: { x: 400, y: 4700 },
        data: {
          label: 'Checklist Concluído',
          message: '*CHECKLIST FINALIZADO!*\n\nVeículo: {{veiculo.placa}}\nKM: {{km_atual}}\nItens verificados: {{total_itens_checados}}\nProblemas encontrados: {{total_problemas}}\nStatus: {{status_checklist}}\n\nObrigado pela colaboração!',
          messageType: 'text'
        }
      },
      // =============================================
      // VERIFICAR SE TEM PROBLEMAS PARA EMAIL
      // =============================================
      {
        id: 'condition-tem-problemas',
        type: 'condition',
        position: { x: 400, y: 4800 },
        data: {
          label: 'Tem Problemas?',
          variable: 'total_problemas',
          operator: 'greater_than',
          value: '0',
          description: 'Verifica se há problemas para notificar gestor'
        }
      },
      // =============================================
      // BUSCAR EMAIL DO GESTOR
      // =============================================
      {
        id: 'action-buscar-gestor',
        type: 'action',
        position: { x: 400, y: 4900 },
        data: {
          label: 'Buscar Gestor',
          description: 'Busca email do gestor da frota',
          actions: {
            output: [
              {
                id: 'api_buscar_gestor',
                type: 'api_call',
                label: 'Buscar Gestor (API)',
                enabled: true,
                order: 1,
                config: {
                  url: '/api/usuarios?cargo=gestor_frota&empresa_id={{empresa_id}}',
                  method: 'GET',
                  headers: '{"Content-Type": "application/json"}',
                  targetVariable: 'gestor_response'
                }
              },
              {
                id: 'extract_gestor_email',
                type: 'extract_field',
                label: 'Extrair Email',
                enabled: true,
                order: 2,
                config: {
                  sourcePath: 'gestor_response.data[0].email',
                  targetVariable: 'gestor_email',
                  defaultValue: ''
                }
              }
            ]
          }
        }
      },
      // =============================================
      // PREPARAR CORPO DO EMAIL
      // =============================================
      {
        id: 'action-preparar-email',
        type: 'action',
        position: { x: 400, y: 5000 },
        data: {
          label: 'Preparar Email',
          description: 'Prepara o corpo do email com lista de problemas',
          actions: {
            output: [
              {
                id: 'montar_lista_problemas',
                type: 'template_render',
                label: 'Montar Lista de Problemas',
                enabled: true,
                order: 1,
                config: {
                  template: `
<h2>Checklist com Problemas - {{veiculo.placa}}</h2>
<p><strong>Veículo:</strong> {{veiculo.placa}} - {{veiculo.modelo}}</p>
<p><strong>KM:</strong> {{km_atual}}</p>
<p><strong>Motorista:</strong> {{motorista.nome}}</p>

<h3>Problemas Encontrados ({{total_problemas}}):</h3>
<table border="1" cellpadding="10" style="border-collapse: collapse;">
  <tr style="background-color: #f2f2f2;">
    <th>Grupo</th>
    <th>Item</th>
    <th>Descrição do Problema</th>
    <th>Ocorrência</th>
  </tr>
  {{#each itens_com_problema}}
  <tr>
    <td>{{this.grupo}}</td>
    <td>{{this.item}}</td>
    <td>{{this.descricao}}</td>
    <td>#{{this.ocorrencia_id}}</td>
  </tr>
  {{/each}}
</table>

<p>Acesse o sistema para visualizar as fotos e mais detalhes.</p>
                  `,
                  outputVariable: 'email_body_html'
                }
              }
            ]
          }
        }
      },
      // =============================================
      // ENVIAR EMAIL PARA O GESTOR
      // =============================================
      {
        id: 'email-gestor',
        type: 'email',
        position: { x: 400, y: 5100 },
        data: {
          label: 'Enviar Email Gestor',
          to: '{{gestor_email}}',
          subject: '[ALERTA] Checklist com {{total_problemas}} problema(s) - {{veiculo.placa}}',
          bodyType: 'html',
          body: '{{email_body_html}}',
          description: 'Envia email detalhado ao gestor com lista de problemas'
        }
      },
      // =============================================
      // FIM DO FLUXO
      // =============================================
      {
        id: 'end-1',
        type: 'end',
        position: { x: 400, y: 5200 },
        data: { label: 'Fim' }
      },
      // =============================================
      // END ALTERNATIVO (sem problemas)
      // =============================================
      {
        id: 'end-sem-problemas',
        type: 'end',
        position: { x: 700, y: 4900 },
        data: { label: 'Fim (sem email)' }
      },
      // =============================================
      // END PARA GRUPO IGNORADO
      // =============================================
      {
        id: 'end-grupo',
        type: 'end',
        position: { x: 900, y: 700 },
        data: { label: 'Fim' }
      },
      // =============================================
      // END PARA VEÍCULO NÃO ENCONTRADO / ERRO API
      // =============================================
      {
        id: 'end-veiculo-nao-encontrado',
        type: 'end',
        position: { x: 1200, y: 900 },
        data: { label: 'Fim' }
      }
    ],
    edges: [
      // Fluxo principal inicial
      { id: 'e1', source: 'start-1', target: 'set-vars-inicial', type: 'smoothstep', animated: true },
      { id: 'e2', source: 'set-vars-inicial', target: 'message-solicitar-gps', type: 'smoothstep' },

      // Captura de GPS e placa
      { id: 'e2a', source: 'message-solicitar-gps', target: 'input-gps', type: 'smoothstep' },
      { id: 'e2b', source: 'input-gps', target: 'action-salvar-gps', type: 'smoothstep' },
      { id: 'e2c', source: 'action-salvar-gps', target: 'message-gps-ok', type: 'smoothstep' },
      { id: 'e2d', source: 'message-gps-ok', target: 'input-placa', type: 'smoothstep' },
      { id: 'e2e', source: 'input-placa', target: 'condition-grupo', type: 'smoothstep' },

      // Verificação de grupo
      { id: 'e3', source: 'condition-grupo', target: 'message-grupo-ignorado', sourceHandle: 'condition-true', type: 'smoothstep', label: 'Sim' },
      { id: 'e4', source: 'condition-grupo', target: 'action-buscar-veiculo', sourceHandle: 'condition-false', type: 'smoothstep', label: 'Não' },
      { id: 'e5', source: 'message-grupo-ignorado', target: 'end-grupo', type: 'smoothstep' },

      // Busca de veículo com verificação de erro de API
      { id: 'e6', source: 'action-buscar-veiculo', target: 'condition-api-error', type: 'smoothstep' },
      { id: 'e6a', source: 'condition-api-error', target: 'condition-veiculo-existe', sourceHandle: 'condition-true', type: 'smoothstep', label: 'OK' },
      { id: 'e6b', source: 'condition-api-error', target: 'message-api-error', sourceHandle: 'condition-false', type: 'smoothstep', label: 'Erro' },
      { id: 'e6c', source: 'message-api-error', target: 'end-veiculo-nao-encontrado', type: 'smoothstep' },

      // Verificação de veículo e busca de motorista
      { id: 'e7', source: 'condition-veiculo-existe', target: 'message-veiculo-nao-encontrado', sourceHandle: 'condition-false', type: 'smoothstep', label: 'Não' },
      { id: 'e8', source: 'condition-veiculo-existe', target: 'action-buscar-motorista', sourceHandle: 'condition-true', type: 'smoothstep', label: 'Sim' },
      { id: 'e8a', source: 'action-buscar-motorista', target: 'action-criar-checklist', type: 'smoothstep' },
      { id: 'e9', source: 'message-veiculo-nao-encontrado', target: 'end-veiculo-nao-encontrado', type: 'smoothstep' },

      // Criação do checklist e busca de grupos
      { id: 'e10', source: 'action-criar-checklist', target: 'action-buscar-grupos', type: 'smoothstep' },
      { id: 'e11', source: 'action-buscar-grupos', target: 'message-solicitar-km', type: 'smoothstep' },

      // Captura de KM e foto
      { id: 'e12', source: 'message-solicitar-km', target: 'input-km-inicial', type: 'smoothstep' },
      { id: 'e13', source: 'input-km-inicial', target: 'action-salvar-km', type: 'smoothstep' },
      { id: 'e14', source: 'action-salvar-km', target: 'message-solicitar-foto-km', type: 'smoothstep' },
      { id: 'e15', source: 'message-solicitar-foto-km', target: 'input-foto-km', type: 'smoothstep' },
      { id: 'e16', source: 'input-foto-km', target: 'action-salvar-foto-km', type: 'smoothstep' },
      { id: 'e17', source: 'action-salvar-foto-km', target: 'loop-grupos', type: 'smoothstep' },

      // Loop de grupos
      { id: 'e18', source: 'loop-grupos', target: 'message-grupo-checklist', sourceHandle: 'loop-item', type: 'smoothstep', label: 'Cada grupo' },
      { id: 'e19', source: 'message-grupo-checklist', target: 'action-buscar-itens-grupo', type: 'smoothstep' },
      { id: 'e20', source: 'action-buscar-itens-grupo', target: 'loop-itens', type: 'smoothstep' },

      // Loop de itens - com reset de tentativas
      { id: 'e21', source: 'loop-itens', target: 'action-reset-tentativas', sourceHandle: 'loop-item', type: 'smoothstep', label: 'Cada item' },
      { id: 'e21a', source: 'action-reset-tentativas', target: 'message-item-checklist', type: 'smoothstep' },
      { id: 'e22', source: 'message-item-checklist', target: 'input-resposta-item', type: 'smoothstep' },

      // Validação de resposta
      { id: 'e22a', source: 'input-resposta-item', target: 'condition-resposta-valida', type: 'smoothstep' },
      { id: 'e22b', source: 'condition-resposta-valida', target: 'condition-item-ok', sourceHandle: 'condition-true', type: 'smoothstep', label: 'Válida' },
      { id: 'e22c', source: 'condition-resposta-valida', target: 'action-incrementar-tentativas', sourceHandle: 'condition-false', type: 'smoothstep', label: 'Inválida' },
      { id: 'e22d', source: 'action-incrementar-tentativas', target: 'condition-max-tentativas', type: 'smoothstep' },
      { id: 'e22e', source: 'condition-max-tentativas', target: 'message-resposta-invalida', sourceHandle: 'condition-false', type: 'smoothstep', label: 'Tentar novamente' },
      { id: 'e22f', source: 'message-resposta-invalida', target: 'input-resposta-item', type: 'smoothstep' },
      { id: 'e22g', source: 'condition-max-tentativas', target: 'message-assumir-ok', sourceHandle: 'condition-true', type: 'smoothstep', label: 'Excedeu' },
      { id: 'e22h', source: 'message-assumir-ok', target: 'action-forcar-ok', type: 'smoothstep' },
      { id: 'e22i', source: 'action-forcar-ok', target: 'condition-item-ok', type: 'smoothstep' },

      // Fluxo OK
      { id: 'e24', source: 'condition-item-ok', target: 'action-salvar-item-ok', sourceHandle: 'condition-true', type: 'smoothstep', label: 'OK' },
      { id: 'e25', source: 'action-salvar-item-ok', target: 'merge-item', type: 'smoothstep' },

      // Fluxo NOK
      { id: 'e26', source: 'condition-item-ok', target: 'message-solicitar-descricao-problema', sourceHandle: 'condition-false', type: 'smoothstep', label: 'NOK' },
      { id: 'e27', source: 'message-solicitar-descricao-problema', target: 'input-descricao-problema', type: 'smoothstep' },
      { id: 'e28', source: 'input-descricao-problema', target: 'message-solicitar-foto-problema', type: 'smoothstep' },
      { id: 'e29', source: 'message-solicitar-foto-problema', target: 'input-foto-problema', type: 'smoothstep' },
      { id: 'e30', source: 'input-foto-problema', target: 'action-salvar-item-nok', type: 'smoothstep' },
      { id: 'e31', source: 'action-salvar-item-nok', target: 'action-criar-ocorrencia', type: 'smoothstep' },
      { id: 'e32', source: 'action-criar-ocorrencia', target: 'action-adicionar-problema-lista', type: 'smoothstep' },
      { id: 'e33', source: 'action-adicionar-problema-lista', target: 'message-ocorrencia-criada', type: 'smoothstep' },
      { id: 'e34', source: 'message-ocorrencia-criada', target: 'merge-item', type: 'smoothstep' },

      // Continuação do loop de itens
      { id: 'e35', source: 'merge-item', target: 'loop-itens-end', type: 'smoothstep' },
      { id: 'e36', source: 'loop-itens-end', target: 'loop-itens', sourceHandle: 'loop-continue', type: 'smoothstep', label: 'Próximo' },
      { id: 'e37', source: 'loop-itens-end', target: 'message-fim-grupo', sourceHandle: 'loop-exit', type: 'smoothstep', label: 'Fim itens' },

      // Continuação do loop de grupos - com reset de contadores
      { id: 'e38', source: 'message-fim-grupo', target: 'action-reset-contadores-grupo', type: 'smoothstep' },
      { id: 'e38a', source: 'action-reset-contadores-grupo', target: 'loop-grupos-end', type: 'smoothstep' },
      { id: 'e39', source: 'loop-grupos-end', target: 'loop-grupos', sourceHandle: 'loop-continue', type: 'smoothstep', label: 'Próximo' },
      { id: 'e40', source: 'loop-grupos-end', target: 'message-solicitar-observacoes', sourceHandle: 'loop-exit', type: 'smoothstep', label: 'Fim grupos' },

      // Finalização
      { id: 'e41', source: 'message-solicitar-observacoes', target: 'input-observacoes', type: 'smoothstep' },
      { id: 'e42', source: 'input-observacoes', target: 'action-finalizar-checklist', type: 'smoothstep' },
      { id: 'e43', source: 'action-finalizar-checklist', target: 'message-checklist-concluido', type: 'smoothstep' },
      { id: 'e44', source: 'message-checklist-concluido', target: 'condition-tem-problemas', type: 'smoothstep' },

      // Envio de email
      { id: 'e45', source: 'condition-tem-problemas', target: 'action-buscar-gestor', sourceHandle: 'condition-true', type: 'smoothstep', label: 'Sim' },
      { id: 'e46', source: 'condition-tem-problemas', target: 'end-sem-problemas', sourceHandle: 'condition-false', type: 'smoothstep', label: 'Não' },
      { id: 'e47', source: 'action-buscar-gestor', target: 'action-preparar-email', type: 'smoothstep' },
      { id: 'e48', source: 'action-preparar-email', target: 'email-gestor', type: 'smoothstep' },
      { id: 'e49', source: 'email-gestor', target: 'end-1', type: 'smoothstep' }
    ]
  },
  // =============================================
  // TEMPLATE: CHECKLIST DE VEÍCULO COM QR CODE
  // Identificação via QR Code, TAG ou Placa
  // =============================================
  {
    id: 'checklist-qrcode-automation',
    name: 'Checklist de Veículo com QR Code',
    description: 'Automação de checklist com identificação de veículo/equipamento via QR Code ou TAG. Permite escanear QR Code pela câmera do WhatsApp, usar scanner web ou digitar TAG/Placa manualmente. Inclui: GPS, validação, notificações e resumo.',
    category: 'frota',
    icon: 'qr-code',
    tags: ['checklist', 'veículo', 'equipamento', 'QR Code', 'TAG', 'whatsapp', 'frota', 'automação', 'GPS'],
    nodes: [
      // =============================================
      // INÍCIO - Recebe dados do WhatsApp
      // =============================================
      {
        id: 'start-1',
        type: 'start',
        position: { x: 400, y: 0 },
        data: {
          label: 'Início',
          description: 'Recebe mensagem do WhatsApp',
          inputParameters: [
            { name: 'phone_number', type: 'string', description: 'Número do telefone do remetente' },
            { name: 'message_text', type: 'string', description: 'Texto da mensagem recebida' },
            { name: 'message_type', type: 'string', description: 'Tipo: text, image, audio, document, location' },
            { name: 'message_media_url', type: 'string', description: 'URL da mídia (se houver)' },
            { name: 'message_id', type: 'string', description: 'ID único da mensagem' },
            { name: 'is_group', type: 'boolean', description: 'Se é mensagem de grupo' },
            { name: 'group_id', type: 'string', description: 'ID do grupo (se for grupo)' },
            { name: 'contact_name', type: 'string', description: 'Nome do contato' },
            { name: 'timestamp', type: 'string', description: 'Data/hora da mensagem' },
            { name: 'empresa_id', type: 'number', description: 'ID da empresa' },
            { name: 'latitude', type: 'number', description: 'Latitude (se localização)' },
            { name: 'longitude', type: 'number', description: 'Longitude (se localização)' }
          ]
        }
      },
      // =============================================
      // DEFINIR VARIÁVEIS INICIAIS
      // =============================================
      {
        id: 'set-vars-inicial',
        type: 'set_variables',
        position: { x: 400, y: 100 },
        data: {
          label: 'Variáveis Iniciais',
          assignments: [
            { id: 'v1', name: 'itens_com_problema', value: '[]', type: 'array' },
            { id: 'v2', name: 'total_itens_checados', value: '0', type: 'number' },
            { id: 'v3', name: 'total_problemas', value: '0', type: 'number' },
            { id: 'v4', name: 'grupo_atual', value: '0', type: 'number' },
            { id: 'v5', name: 'item_atual', value: '0', type: 'number' },
            { id: 'v6', name: 'checklist_iniciado', value: 'false', type: 'boolean' },
            { id: 'v7', name: 'itens_ok_grupo', value: '0', type: 'number' },
            { id: 'v8', name: 'itens_nok_grupo', value: '0', type: 'number' },
            { id: 'v9', name: 'tentativas_resposta', value: '0', type: 'number' },
            { id: 'v10', name: 'max_tentativas', value: '3', type: 'number' },
            { id: 'v11', name: 'observacoes_gerais', value: '', type: 'string' },
            { id: 'v12', name: 'total_grupos', value: '0', type: 'number' },
            { id: 'v13', name: 'grupo_index', value: '0', type: 'number' },
            { id: 'v14', name: 'item_index', value: '0', type: 'number' },
            { id: 'v15', name: 'foto_item_url', value: '', type: 'string' },
            { id: 'v16', name: 'descricao_problema', value: '', type: 'string' },
            { id: 'v17', name: 'status_checklist', value: 'em_andamento', type: 'string' },
            { id: 'v18', name: 'observacoes_final', value: '', type: 'string' },
            { id: 'v19', name: 'scanner_url', value: '', type: 'string' },
            { id: 'v20', name: 'codigo_equipamento', value: '', type: 'string' }
          ],
          description: 'Inicializa contadores e arrays para o checklist'
        }
      },
      // =============================================
      // SOLICITAR LOCALIZAÇÃO GPS
      // =============================================
      {
        id: 'message-solicitar-gps',
        type: 'message',
        position: { x: 400, y: 200 },
        data: {
          label: 'Solicitar GPS',
          message: '*CHECKLIST DE VEÍCULO/EQUIPAMENTO*\n\nPara iniciar, compartilhe sua *localização atual*.\n\nClique no ícone de anexo (📎) e selecione "Localização".',
          messageType: 'text'
        }
      },
      {
        id: 'input-gps',
        type: 'input',
        position: { x: 400, y: 300 },
        data: {
          label: 'Capturar GPS',
          inputType: 'location',
          variableName: 'localizacao_inicial',
          description: 'Captura localização GPS do motorista',
          timeout: 120000,
          timeoutMessage: 'Tempo esgotado aguardando localização. Por favor, compartilhe sua localização para continuar.'
        }
      },
      {
        id: 'action-salvar-gps',
        type: 'action',
        position: { x: 400, y: 400 },
        data: {
          label: 'Salvar GPS',
          description: 'Armazena coordenadas GPS',
          actions: {
            output: [
              {
                id: 'store_latitude',
                type: 'store_variable',
                label: 'Salvar Latitude',
                enabled: true,
                order: 1,
                config: {
                  variableName: 'latitude_inicial',
                  value: '{{latitude}}',
                  scope: 'flow'
                }
              },
              {
                id: 'store_longitude',
                type: 'store_variable',
                label: 'Salvar Longitude',
                enabled: true,
                order: 2,
                config: {
                  variableName: 'longitude_inicial',
                  value: '{{longitude}}',
                  scope: 'flow'
                }
              }
            ]
          }
        }
      },
      // =============================================
      // CONFIGURAR URL DO SCANNER QR CODE
      // =============================================
      {
        id: 'action-set-scanner-url',
        type: 'action',
        position: { x: 400, y: 500 },
        data: {
          label: 'Configurar URL Scanner',
          description: 'Define URL do scanner QR Code',
          actions: {
            output: [
              {
                id: 'set_scanner_url',
                type: 'store_variable',
                label: 'Definir URL Scanner',
                enabled: true,
                order: 1,
                config: {
                  variableName: 'scanner_url',
                  value: '{{system.frontend_url}}/qr-scanner?phone={{phone_number}}',
                  scope: 'flow'
                }
              }
            ]
          }
        }
      },
      {
        id: 'message-gps-ok',
        type: 'message',
        position: { x: 400, y: 600 },
        data: {
          label: 'Solicitar QR/TAG',
          message: '📍 Localização registrada!\n\nAgora, identifique o veículo/equipamento:\n\n📱 *Opção 1:* Escaneie o QR Code usando a câmera do WhatsApp\n\n🌐 *Opção 2:* Acesse o scanner web:\n{{scanner_url}}\n\n⌨️ *Opção 3:* Digite a TAG ou Placa manualmente',
          messageType: 'text'
        }
      },
      {
        id: 'input-qrcode',
        type: 'input',
        position: { x: 400, y: 700 },
        data: {
          label: 'Capturar QR/TAG',
          inputType: 'text',
          variableName: 'codigo_equipamento',
          description: 'Captura QR Code, TAG ou Placa do veículo/equipamento',
          timeout: 120000,
          timeoutMessage: 'Tempo esgotado. Por favor, escaneie o QR Code ou digite a TAG/Placa.'
        }
      },
      // =============================================
      // VERIFICAR SE É MENSAGEM DE GRUPO
      // =============================================
      {
        id: 'condition-grupo',
        type: 'condition',
        position: { x: 400, y: 800 },
        data: {
          label: 'É Grupo?',
          variable: 'is_group',
          operator: 'equals',
          value: 'true',
          description: 'Ignora mensagens de grupos'
        }
      },
      {
        id: 'message-grupo-ignorado',
        type: 'message',
        position: { x: 700, y: 800 },
        data: {
          label: 'Grupo Ignorado',
          message: 'Mensagens de grupos não são processadas. Envie no privado.',
          messageType: 'text'
        }
      },
      // =============================================
      // BUSCAR VEÍCULO/EQUIPAMENTO PELO CÓDIGO
      // =============================================
      {
        id: 'action-buscar-veiculo',
        type: 'action',
        position: { x: 400, y: 900 },
        data: {
          label: 'Buscar Equipamento',
          description: 'Busca veículo/equipamento pelo código QR/TAG/Placa',
          actions: {
            input: [
              {
                id: 'transform_codigo',
                type: 'transform_uppercase',
                label: 'Converter para Maiúsculo',
                enabled: true,
                order: 1,
                config: { field: 'codigo_equipamento' }
              }
            ],
            output: [
              {
                id: 'api_buscar_equipamento',
                type: 'api_call',
                label: 'Buscar por QR/TAG (API Pública)',
                enabled: true,
                order: 1,
                config: {
                  url: '/api/public/qr/scan',
                  method: 'POST',
                  headers: '{"Content-Type": "application/json"}',
                  body: '{"qr_data": "{{codigo_equipamento}}"}',
                  targetVariable: 'equipamento_response',
                  timeout: 10000,
                  retryOnError: true,
                  maxRetries: 2
                }
              },
              {
                id: 'extract_equipamento',
                type: 'extract_field',
                label: 'Extrair Equipamento',
                enabled: true,
                order: 2,
                config: {
                  sourcePath: 'equipamento_response.data',
                  targetVariable: 'equipamento',
                  defaultValue: null
                }
              },
              {
                id: 'extract_veiculo',
                type: 'extract_field',
                label: 'Extrair Veículo',
                enabled: true,
                order: 3,
                config: {
                  sourcePath: 'equipamento_response.data',
                  targetVariable: 'veiculo',
                  defaultValue: null
                }
              },
              {
                id: 'extract_tipo',
                type: 'extract_field',
                label: 'Extrair Tipo',
                enabled: true,
                order: 4,
                config: {
                  sourcePath: 'equipamento_response.data.tipo',
                  targetVariable: 'tipo_identificacao',
                  defaultValue: 'EQUIPAMENTO'
                }
              },
              {
                id: 'check_api_error',
                type: 'extract_field',
                label: 'Verificar Erro API',
                enabled: true,
                order: 5,
                config: {
                  sourcePath: 'equipamento_response.success',
                  targetVariable: 'api_success',
                  defaultValue: false
                }
              }
            ]
          }
        }
      },
      // =============================================
      // VERIFICAR ERRO NA API
      // =============================================
      {
        id: 'condition-api-error',
        type: 'condition',
        position: { x: 400, y: 1000 },
        data: {
          label: 'API OK?',
          variable: 'api_success',
          operator: 'equals',
          value: 'true',
          description: 'Verifica se a API respondeu sem erro'
        }
      },
      {
        id: 'message-api-error',
        type: 'message',
        position: { x: 700, y: 950 },
        data: {
          label: 'Erro na API',
          message: 'Desculpe, ocorreu um erro ao buscar o veículo/equipamento. Tente novamente em alguns instantes.',
          messageType: 'text'
        }
      },
      // =============================================
      // VERIFICAR SE EQUIPAMENTO EXISTE
      // =============================================
      {
        id: 'condition-veiculo-existe',
        type: 'condition',
        position: { x: 400, y: 1100 },
        data: {
          label: 'Equipamento Existe?',
          variable: 'equipamento',
          operator: 'not_null',
          value: '',
          description: 'Verifica se encontrou o veículo/equipamento'
        }
      },
      {
        id: 'message-veiculo-nao-encontrado',
        type: 'message',
        position: { x: 1000, y: 1000 },
        data: {
          label: 'Equipamento Não Encontrado',
          message: 'Veículo/Equipamento com código "{{codigo_equipamento}}" não encontrado no sistema.\n\nVerifique o QR Code/TAG e tente novamente.',
          messageType: 'text'
        }
      },
      // =============================================
      // EQUIPAMENTO IDENTIFICADO - MOSTRAR INFO
      // =============================================
      {
        id: 'message-equipamento-encontrado',
        type: 'message',
        position: { x: 400, y: 1200 },
        data: {
          label: 'Equipamento Identificado',
          message: '✅ *Equipamento Identificado!*\n\n*{{equipamento.nome}}*\nTipo: {{equipamento.tipo}}\nID: {{equipamento.identificador}}\n\nIniciando checklist...',
          messageType: 'text'
        }
      },
      // =============================================
      // BUSCAR MOTORISTA PELO TELEFONE
      // =============================================
      {
        id: 'action-buscar-motorista',
        type: 'action',
        position: { x: 400, y: 1300 },
        data: {
          label: 'Buscar Motorista',
          description: 'Busca motorista pelo número de telefone (API Pública)',
          actions: {
            output: [
              {
                id: 'api_buscar_motorista',
                type: 'api_call',
                label: 'Buscar Motorista (API Pública)',
                enabled: true,
                order: 1,
                config: {
                  url: '/api/public/qr/motorista',
                  method: 'POST',
                  headers: '{"Content-Type": "application/json"}',
                  body: '{"telefone": "{{phone_number}}", "empresa_id": {{equipamento.empresa_id}}}',
                  targetVariable: 'motorista_response',
                  timeout: 10000,
                  retryOnError: true,
                  maxRetries: 2
                }
              },
              {
                id: 'extract_motorista',
                type: 'extract_field',
                label: 'Extrair Motorista',
                enabled: true,
                order: 2,
                config: {
                  sourcePath: 'motorista_response.data',
                  targetVariable: 'motorista',
                  defaultValue: null
                }
              }
            ]
          }
        }
      },
      // =============================================
      // CRIAR CHECKLIST
      // =============================================
      {
        id: 'action-criar-checklist',
        type: 'action',
        position: { x: 400, y: 1400 },
        data: {
          label: 'Criar Checklist',
          description: 'Cria checklist via API Pública de QR Code',
          actions: {
            output: [
              {
                id: 'api_criar_checklist',
                type: 'api_call',
                label: 'Criar Checklist (API Pública)',
                enabled: true,
                order: 1,
                config: {
                  url: '/api/public/qr/checklist',
                  method: 'POST',
                  headers: '{"Content-Type": "application/json"}',
                  body: '{"motorista_telefone": "{{phone_number}}", "tipo_identificacao": "{{tipo_identificacao}}", "identificacao_id": {{equipamento.id}}, "localizacao": {"latitude": {{latitude_inicial}}, "longitude": {{longitude_inicial}}}}',
                  targetVariable: 'checklist_response',
                  timeout: 10000
                }
              },
              {
                id: 'extract_checklist_id',
                type: 'extract_field',
                label: 'Extrair ID do Checklist',
                enabled: true,
                order: 2,
                config: {
                  sourcePath: 'checklist_response.data.checklist_id',
                  targetVariable: 'checklist_id',
                  defaultValue: null
                }
              },
              {
                id: 'extract_checklist_motorista',
                type: 'extract_field',
                label: 'Extrair Motorista do Checklist',
                enabled: true,
                order: 3,
                config: {
                  sourcePath: 'checklist_response.data.motorista',
                  targetVariable: 'motorista',
                  defaultValue: null
                }
              }
            ]
          }
        }
      },
      // =============================================
      // BUSCAR GRUPOS DE ITENS DO CHECKLIST
      // =============================================
      {
        id: 'action-buscar-grupos',
        type: 'action',
        position: { x: 400, y: 1500 },
        data: {
          label: 'Buscar Grupos',
          description: 'Busca grupos de itens do template de checklist',
          actions: {
            output: [
              {
                id: 'api_buscar_grupos',
                type: 'api_call',
                label: 'Buscar Grupos (API)',
                enabled: true,
                order: 1,
                config: {
                  url: '/api/checklist-templates/{{equipamento.tipo}}/grupos',
                  method: 'GET',
                  headers: '{"Content-Type": "application/json"}',
                  targetVariable: 'grupos_response',
                  timeout: 10000
                }
              },
              {
                id: 'extract_grupos',
                type: 'extract_field',
                label: 'Extrair Grupos',
                enabled: true,
                order: 2,
                config: {
                  sourcePath: 'grupos_response.data',
                  targetVariable: 'grupos_checklist',
                  defaultValue: []
                }
              },
              {
                id: 'count_grupos',
                type: 'array_length',
                label: 'Contar Grupos',
                enabled: true,
                order: 3,
                config: {
                  sourceArray: 'grupos_checklist',
                  targetVariable: 'total_grupos'
                }
              }
            ]
          }
        }
      },
      // =============================================
      // SOLICITAR QUILOMETRAGEM/HORÍMETRO
      // =============================================
      {
        id: 'message-solicitar-km',
        type: 'message',
        position: { x: 400, y: 1600 },
        data: {
          label: 'Solicitar KM/Horímetro',
          message: '📊 Informe a *quilometragem* ou *horímetro* atual:',
          messageType: 'text'
        }
      },
      {
        id: 'input-km-inicial',
        type: 'input',
        position: { x: 400, y: 1700 },
        data: {
          label: 'Capturar KM',
          inputType: 'number',
          variableName: 'km_inicial',
          description: 'Captura quilometragem ou horímetro atual',
          timeout: 60000,
          timeoutMessage: 'Tempo esgotado. Por favor, informe a quilometragem/horímetro.'
        }
      },
      // =============================================
      // SOLICITAR FOTO DO PAINEL
      // =============================================
      {
        id: 'message-solicitar-foto-km',
        type: 'message',
        position: { x: 400, y: 1800 },
        data: {
          label: 'Solicitar Foto Painel',
          message: '📸 Envie uma *foto do painel* mostrando a quilometragem/horímetro:',
          messageType: 'text'
        }
      },
      {
        id: 'input-foto-km',
        type: 'input',
        position: { x: 400, y: 1900 },
        data: {
          label: 'Capturar Foto Painel',
          inputType: 'image',
          variableName: 'foto_painel',
          description: 'Captura foto do painel com KM/horímetro',
          timeout: 120000,
          timeoutMessage: 'Tempo esgotado. Por favor, envie a foto do painel.'
        }
      },
      // =============================================
      // MENSAGEM DE INÍCIO DOS GRUPOS
      // =============================================
      {
        id: 'message-inicio-checklist',
        type: 'message',
        position: { x: 400, y: 2000 },
        data: {
          label: 'Início Checklist',
          message: '📋 *INÍCIO DO CHECKLIST*\n\nResponda cada item com:\n✅ *OK* - Item conforme\n❌ *NOK* - Item com problema\n\nVamos começar!',
          messageType: 'text'
        }
      },
      // =============================================
      // FIM - SUCESSO
      // =============================================
      {
        id: 'end-1',
        type: 'end',
        position: { x: 400, y: 2100 },
        data: {
          label: 'Fim - Checklist Iniciado',
          description: 'Checklist criado e pronto para responder itens'
        }
      },
      // =============================================
      // FIM - GRUPO IGNORADO
      // =============================================
      {
        id: 'end-grupo',
        type: 'end',
        position: { x: 700, y: 900 },
        data: {
          label: 'Fim - Grupo',
          description: 'Mensagem de grupo ignorada'
        }
      },
      // =============================================
      // FIM - EQUIPAMENTO NÃO ENCONTRADO
      // =============================================
      {
        id: 'end-equipamento-nao-encontrado',
        type: 'end',
        position: { x: 1000, y: 1100 },
        data: {
          label: 'Fim - Não Encontrado',
          description: 'Equipamento não encontrado no sistema'
        }
      },
      // =============================================
      // FIM - ERRO API
      // =============================================
      {
        id: 'end-api-error',
        type: 'end',
        position: { x: 700, y: 1050 },
        data: {
          label: 'Fim - Erro API',
          description: 'Erro ao consultar API'
        }
      }
    ],
    edges: [
      // Início e variáveis
      { id: 'e1', source: 'start-1', target: 'set-vars-inicial', type: 'smoothstep', animated: true },
      { id: 'e2', source: 'set-vars-inicial', target: 'message-solicitar-gps', type: 'smoothstep' },

      // Captura de GPS e QR Code
      { id: 'e2a', source: 'message-solicitar-gps', target: 'input-gps', type: 'smoothstep' },
      { id: 'e2b', source: 'input-gps', target: 'action-salvar-gps', type: 'smoothstep' },
      { id: 'e2c', source: 'action-salvar-gps', target: 'action-set-scanner-url', type: 'smoothstep' },
      { id: 'e2c2', source: 'action-set-scanner-url', target: 'message-gps-ok', type: 'smoothstep' },
      { id: 'e2d', source: 'message-gps-ok', target: 'input-qrcode', type: 'smoothstep' },
      { id: 'e2e', source: 'input-qrcode', target: 'condition-grupo', type: 'smoothstep' },

      // Verificação de grupo
      { id: 'e3', source: 'condition-grupo', target: 'message-grupo-ignorado', sourceHandle: 'condition-true', type: 'smoothstep', label: 'Sim' },
      { id: 'e4', source: 'condition-grupo', target: 'action-buscar-veiculo', sourceHandle: 'condition-false', type: 'smoothstep', label: 'Não' },
      { id: 'e5', source: 'message-grupo-ignorado', target: 'end-grupo', type: 'smoothstep' },

      // Busca de equipamento com verificação de erro de API
      { id: 'e6', source: 'action-buscar-veiculo', target: 'condition-api-error', type: 'smoothstep' },
      { id: 'e6a', source: 'condition-api-error', target: 'condition-veiculo-existe', sourceHandle: 'condition-true', type: 'smoothstep', label: 'OK' },
      { id: 'e6b', source: 'condition-api-error', target: 'message-api-error', sourceHandle: 'condition-false', type: 'smoothstep', label: 'Erro' },
      { id: 'e6c', source: 'message-api-error', target: 'end-api-error', type: 'smoothstep' },

      // Verificação de equipamento e fluxo
      { id: 'e7', source: 'condition-veiculo-existe', target: 'message-veiculo-nao-encontrado', sourceHandle: 'condition-false', type: 'smoothstep', label: 'Não' },
      { id: 'e8', source: 'condition-veiculo-existe', target: 'message-equipamento-encontrado', sourceHandle: 'condition-true', type: 'smoothstep', label: 'Sim' },
      { id: 'e8a', source: 'message-equipamento-encontrado', target: 'action-buscar-motorista', type: 'smoothstep' },
      { id: 'e8b', source: 'action-buscar-motorista', target: 'action-criar-checklist', type: 'smoothstep' },
      { id: 'e9', source: 'message-veiculo-nao-encontrado', target: 'end-equipamento-nao-encontrado', type: 'smoothstep' },

      // Criação do checklist e busca de grupos
      { id: 'e10', source: 'action-criar-checklist', target: 'action-buscar-grupos', type: 'smoothstep' },
      { id: 'e11', source: 'action-buscar-grupos', target: 'message-solicitar-km', type: 'smoothstep' },

      // Captura de KM e foto
      { id: 'e12', source: 'message-solicitar-km', target: 'input-km-inicial', type: 'smoothstep' },
      { id: 'e13', source: 'input-km-inicial', target: 'message-solicitar-foto-km', type: 'smoothstep' },
      { id: 'e14', source: 'message-solicitar-foto-km', target: 'input-foto-km', type: 'smoothstep' },
      { id: 'e15', source: 'input-foto-km', target: 'message-inicio-checklist', type: 'smoothstep' },
      { id: 'e16', source: 'message-inicio-checklist', target: 'end-1', type: 'smoothstep' }
    ]
  }
];

export default flowTemplates;
